import { Injectable, inject } from '@angular/core';
import {
  BasesiteCurrencyMap,
  CookieService,
  CultureLanguageMap,
  LocalStorageService,
} from '@boels/helpers';
import { BoelsCultureState, StorageKey } from '@boels/models';
import { TranslocoService } from '@jsverse/transloco';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { SiteContextFacade } from '../../site-context/facade/site-context.facade';
import * as CulturesAction from '../actions/cultures.actions';
import {
  getActiveCulture,
  getActiveCurrency,
  getActiveLanguage,
} from '../selectors/cultures.selector';

@Injectable({
  providedIn: 'root',
})
export class CultureFacade {
  private readonly store = inject<Store<BoelsCultureState>>(Store);
  private translocoService = inject(TranslocoService);
  private siteContextFacade = inject(SiteContextFacade);
  private localStorageService = inject(LocalStorageService);
  private cookieService = inject(CookieService);

  private readonly baseSiteUidPrefix = 'rental';

  getActiveCulture(): Observable<string> {
    return this.store.pipe(select(getActiveCulture));
  }

  getActiveLanguage(): Observable<string> {
    return this.store.pipe(select(getActiveLanguage));
  }

  getActiveCurrency(): Observable<string> {
    return this.store.pipe(select(getActiveCurrency));
  }

  setActiveContext(culture: string): void {
    const baseSite = `rental${culture.split('-')[1].toUpperCase()}`;
    const currency =
      BasesiteCurrencyMap[baseSite as keyof typeof BasesiteCurrencyMap];
    const language =
      CultureLanguageMap[culture as keyof typeof CultureLanguageMap];

    this.setActiveCurrency(currency);
    this.setActiveLanguage(language);
    this.siteContextFacade.setBaseSite(baseSite);
    this.setActiveCulture(culture);
  }

  setGenericContext(language: string): void {
    const baseSite = language === 'en' ? 'Generic' : language.toUpperCase();
    this.setActiveCurrency('EUR');
    this.setActiveLanguage(language);
    this.setBaseSite(baseSite);
  }

  setActiveCulture(culture: string): void {
    this.localStorageService.setItem(StorageKey.CULTURE, culture);
    this.cookieService.setCookie(StorageKey.CULTURE, culture, 3000);
    this.store.dispatch(new CulturesAction.SetActiveCulture(culture));
  }

  setActiveLanguage(language: string): void {
    this.translocoService.setActiveLang(language.slice(0, 2));
    this.store.dispatch(new CulturesAction.SetActiveLanguage(language));
  }

  setActiveCurrency(currency: string): void {
    this.localStorageService.setItem(StorageKey.CURRENCY, currency);
    this.store.dispatch(new CulturesAction.SetActiveCurrency(currency));
  }

  setBaseSite(isoCode: string): void {
    this.siteContextFacade.setBaseSite(this.baseSiteUidPrefix + isoCode);
  }
}
